import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, ChapterLabel } from "@/components/site/Reveal";
import { IMAGES, PARTNER, STATS_ENDPOINT, IN_CARE_FALLBACK, RELEASED_FALLBACK } from "@/lib/content";

export default function UeberMich() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);

  // Live-Zahlen aus Rescue Center Protokoll+ laden (mit sicherem Rückfall).
  const [inCare, setInCare] = useState(IN_CARE_FALLBACK);
  const [freed, setFreed] = useState(RELEASED_FALLBACK);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(STATS_ENDPOINT, { headers: { Accept: "application/json" } });
        if (!res.ok) return;
        const data = await res.json();
        const care = data.in_pflege ?? data.in_care ?? data.pflege;
        const released = data.freigelassen ?? data.released ?? data.freed;
        if (active && typeof care === "number") setInCare(care);
        if (active && typeof released === "number") setFreed(released);
      } catch (e) {
        // Endpunkt (noch) nicht erreichbar -> Rückfall-Zahlen bleiben bestehen.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const stats = [
    { value: String(inCare), label: "In Pflege", testId: "stat-in-pflege" },
    { value: String(freed), label: "Freigelassen", testId: "stat-freigelassen" },
    { value: "100%", label: "aus Herzblut", testId: "stat-herzblut" },
  ];

  return (
    <section
      id="ueber-mich"
      data-testid="section-ueber-mich"
      className="relative py-20 md:py-32 bg-cream-alt"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <ChapterLabel index={2} label="Über mich & die Station" color="text-brand-pink" />
          <Reveal className="mt-6">
            <h2 className="font-heading font-bold text-ink text-3xl md:text-4xl lg:text-5xl tracking-tight text-balance">
              Eine kleine Station mit einem großen Herzen
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="mt-6 space-y-5 max-w-xl">
            <p className="text-lg md:text-xl leading-relaxed text-ink-muted">
              Bei mir kümmere ich mich mit viel Liebe und Geduld um Igel, die
              Hilfe brauchen. Aktuell sind es 21 stachelige Gäste, davon fünf
              Intensivpatienten, die besonders viel Aufmerksamkeit bekommen.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-ink-muted">
              Meine Arbeit ist rein privat. Ich päppele auf, pflege, wärme und
              lasse wieder frei – Schritt für Schritt, mit ganzem Herzen. Diese
              Seite baue ich nach und nach aus, damit du hier alles Wichtige an
              einem Ort findest.
            </p>
            <p className="font-accent text-3xl md:text-4xl text-brand-green -rotate-1">
              Mit Liebe. Mit Geduld. Für die Stachelherzen.
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-lg">
            {stats.map((s, i) => (
              <Reveal
                key={s.label}
                delay={0.15 + i * 0.08}
                data-testid={s.testId}
                className="rounded-2xl bg-white p-3 sm:p-5 text-center shadow-soft border border-ink/5"
              >
                <div className="font-heading font-extrabold text-2xl sm:text-3xl md:text-4xl text-brand-orange">
                  {s.value}
                </div>
                <div className="mt-1 text-xs sm:text-sm text-ink-muted font-medium">{s.label}</div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <a
              href={PARTNER.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="partner-badge"
              className="mt-8 inline-flex items-center gap-4 rounded-2xl bg-white p-4 pr-6 shadow-soft border border-ink/5 transition-transform duration-300 hover:-translate-y-1 max-w-md"
            >
              <img
                src={PARTNER.logo}
                alt="Offizieller Kooperationspartner – Rescue Center Protokoll+"
                className="w-16 h-16 md:w-24 md:h-24 object-contain shrink-0"
              />
              <span>
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-green">
                  Offizieller Tierschutzpartner
                </span>
                <span className="block mt-1 font-heading font-bold text-ink text-lg leading-snug">
                  Kooperationspartner von {PARTNER.name}
                </span>
                <span className="block mt-1 text-sm text-ink-muted">
                  Gemeinsam für Tiere. Gemeinsam stärker.
                </span>
              </span>
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <div className="relative overflow-hidden rounded-[3rem] shadow-card aspect-[4/5]">
            <motion.img
              src={IMAGES.about}
              alt="Igel in der Pflege zwischen Herbstlaub"
              style={{ y }}
              className="absolute inset-0 w-full h-[115%] object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
